import React, { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import { groupEntriesByDate, formatDecimalHours } from './timeTrackerUtils';
import type { TimeTrackerEntry, TimeTrackerProject } from '@/types/pm-types';
import { pmTheme } from './pmTheme';

interface TimeTrackerSummaryChartsProps {
  entries: TimeTrackerEntry[];
  projects: TimeTrackerProject[];
  accentColor?: string;
}

export const TimeTrackerSummaryCharts: React.FC<TimeTrackerSummaryChartsProps> = ({
  entries,
  projects,
  accentColor = '#10b981',
}) => {
  // Bar chart: hours per day, stacked by project
  const barData = useMemo(() => {
    const grouped = groupEntriesByDate(entries);
    const dates = [...grouped.keys()].sort();
    return dates.map((date) => {
      const dayEntries = grouped.get(date) || [];
      const row: Record<string, number | string> = { date: new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) };
      for (const entry of dayEntries) {
        const projectName = projects.find((p) => p.id === entry.projectId)?.name || 'No Project';
        row[projectName] = ((row[projectName] as number) || 0) + entry.duration / 3600;
      }
      return row;
    });
  }, [entries, projects]);

  // Pie chart: hours by project
  const pieData = useMemo(() => {
    const totals = new Map<string, number>();
    for (const entry of entries) {
      const project = projects.find((p) => p.id === entry.projectId);
      const name = project?.name || 'No Project';
      totals.set(name, (totals.get(name) || 0) + entry.duration / 3600);
    }
    return [...totals.entries()].map(([name, hours]) => ({ name, hours: Math.round(hours * 10) / 10 }));
  }, [entries, projects]);

  const projectColors = useMemo(() => {
    const colors: Record<string, string> = { 'No Project': pmTheme.textSubtle };
    for (const p of projects) {
      colors[p.name] = p.color;
    }
    return colors;
  }, [projects]);

  const projectNames = useMemo(() => {
    const names = new Set<string>();
    for (const entry of entries) {
      const name = projects.find((p) => p.id === entry.projectId)?.name || 'No Project';
      names.add(name);
    }
    return [...names];
  }, [entries, projects]);

  if (entries.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: pmTheme.textSubtle, fontSize: 14 }}>
        No data to display. Add time entries to see charts.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: 16 }}>
      {/* Bar chart */}
      <div>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: pmTheme.text, marginBottom: 8 }}>Hours by Day</h4>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={barData}>
            <CartesianGrid strokeDasharray="3 3" stroke={pmTheme.borderSubtle} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: pmTheme.textMuted }} />
            <YAxis tick={{ fontSize: 11, fill: pmTheme.textMuted }} tickFormatter={(v: number) => `${v}h`} />
            <Tooltip formatter={(value: number) => formatDecimalHours(value * 3600)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {projectNames.map((name) => (
              <Bar key={name} dataKey={name} stackId="a" fill={projectColors[name] || accentColor} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Pie chart */}
      <div>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: pmTheme.text, marginBottom: 8 }}>Hours by Project</h4>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="hours"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={80}
              label={({ name, hours }: { name: string; hours: number }) => `${name}: ${hours}h`}
              labelLine={{ strokeWidth: 1 }}
            >
              {pieData.map((entry) => (
                <Cell key={entry.name} fill={projectColors[entry.name] || accentColor} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number) => `${value}h`} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

TimeTrackerSummaryCharts.displayName = 'TimeTrackerSummaryCharts';
